// src/components/admin/UserStats.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";

const UserStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalFarmers: 0,
    totalAgribusinesses: 0,
    totalUsers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState("");

  const fetchUserStats = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setMessage("");

      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

      console.log("🔄 Fetching user stats...");

      const response = await fetch(`${API_URL}/api/admin/stats/user-stats`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token && { Authorization: `Bearer ${user.token}` }),
        },
      });

      const result = await response.json();

      if (result.success) {
        setStats(result.data);
        if (result.message) setMessage(result.message);
        console.log("✅ Stats loaded:", result.data);
      } else {
        throw new Error(result.message || "Failed to fetch stats");
      }
    } catch (err) {
      console.error("❌ Error fetching stats:", err);
      // Use mock data as fallback
      setStats({
        totalFarmers: 856,
        totalAgribusinesses: 391,
        totalUsers: 1247,
      });
      setMessage("Using demo data - " + err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUserStats();
  }, []);

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-teal-100 p-6 animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="h-6 w-40 bg-gray-200 rounded-lg mb-2 animate-pulse"></div>
            <div className="h-4 w-24 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <div className="h-8 w-20 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>

        <div className="space-y-4">
          {/* Total Users Skeleton */}
          <div className="p-5 bg-teal-50 rounded-xl border border-teal-100 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-4 w-32 bg-teal-200 rounded"></div>
                <div className="h-8 w-24 bg-teal-300 rounded"></div>
              </div>
              <div className="w-12 h-12 bg-teal-200 rounded-full"></div>
            </div>
          </div>

          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2].map((item) => (
              <div
                key={item}
                className="p-4 rounded-xl border border-gray-100 animate-pulse"
              >
                <div className="w-10 h-10 bg-gray-200 rounded-full mx-auto mb-3"></div>
                <div className="h-4 w-20 bg-gray-200 rounded mx-auto mb-2"></div>
                <div className="h-6 w-16 bg-gray-300 rounded mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-teal-100 p-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            User Statistics
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Real-time platform user overview
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchUserStats(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm"
          >
            <svg
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Farmers & Agribusinesses Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {/* Farmers Card */}
        <div className="group p-5 bg-white rounded-xl border border-green-100 hover:border-green-300 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md group-hover:shadow-lg transition-shadow duration-300">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9"
                />
              </svg>
            </div>
            <p className="text-sm font-semibold text-green-700 mb-1">Farmers</p>
            <p className="text-2xl font-bold text-green-900 mb-2">
              {stats.totalFarmers.toLocaleString()}
            </p>
            <div className="w-full bg-green-100 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-green-500 to-teal-500 h-2 rounded-full transition-all duration-1000 ease-out"
                style={{
                  width: `${Math.min(
                    (stats.totalFarmers / stats.totalUsers) * 100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
            <p className="text-xs text-green-600 mt-2">
              {((stats.totalFarmers / stats.totalUsers) * 100).toFixed(1)}% of
              total users
            </p>
          </div>
        </div>

        {/* Agribusinesses Card */}
        <div className="group p-5 bg-white rounded-xl border border-blue-100 hover:border-blue-300 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
          <div className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-teal-400 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md group-hover:shadow-lg transition-shadow duration-300">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <p className="text-sm font-semibold text-blue-700 mb-1">
              Agribusinesses
            </p>
            <p className="text-2xl font-bold text-blue-900 mb-2">
              {stats.totalAgribusinesses.toLocaleString()}
            </p>
            <div className="w-full bg-blue-100 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-teal-400 h-2 rounded-full transition-all duration-1000 ease-out"
                style={{
                  width: `${Math.min(
                    (stats.totalAgribusinesses / stats.totalUsers) * 100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              {((stats.totalAgribusinesses / stats.totalUsers) * 100).toFixed(
                1
              )}
              % of total users
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {message
            ? "Demo data for testing purposes"
            : "Live database counts from production"}
        </div>

        <div className="flex items-center gap-1 text-xs text-gray-400">
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Auto-refreshes every 5 minutes
        </div>
      </div>
    </div>
  );
};

export default UserStats;
